// Campaign Form Component (Create/Edit)
import React, { useState, useEffect } from 'react';
import { useCampaign } from '../../context/CampaignContext';
import { useAuth } from '../../context/AuthContext';
import { CAMPAIGN_TYPES, CAMPAIGN_STATUS, CAMPAIGN_POSITIONS } from '../../data/campaignModels';
import { Timestamp } from 'firebase/firestore';
import BannerImageUpload from './BannerImageUpload';

const CampaignForm = ({ campaign, onSave, onCancel }) => {
  const { createCampaign, updateCampaign } = useCampaign();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: { th: '', en: '' },
    type: CAMPAIGN_TYPES.PROMOTION,
    startDate: '',
    endDate: '',
    banner: {
      desktop: { th: { imageUrl: '', linkUrl: '', action: 'open-modal' }, en: { imageUrl: '', linkUrl: '', action: 'open-modal' } },
      mobile: { th: { imageUrl: '', linkUrl: '', action: 'open-modal' }, en: { imageUrl: '', linkUrl: '', action: 'open-modal' } }
    },
    features: {
      bonusCoins: { enabled: false, amount: 0, coinType: 'virtual', claimLimit: 1, claimOncePerUser: true },
      packageTrial: { enabled: false, packageType: 'premium', trialDays: 14 }
    },
    positions: [{ positionId: CAMPAIGN_POSITIONS.HOMEPAGE_TOP, priority: 1, enabled: true }],
    targeting: { roles: ['user', 'farm'], userTypes: ['all'], languages: ['th', 'en'] },
    isActive: true
  });

  useEffect(() => {
    if (campaign) {
      // Edit mode - load existing data
      setFormData({
        name: campaign.name || { th: '', en: '' },
        type: campaign.type || CAMPAIGN_TYPES.PROMOTION,
        startDate: campaign.startDate?.toDate?.() ? campaign.startDate.toDate().toISOString().split('T')[0] : '',
        endDate: campaign.endDate?.toDate?.() ? campaign.endDate.toDate().toISOString().split('T')[0] : '',
        banner: campaign.banner ? {
          desktop: {
            th: {
              imageUrl: campaign.banner.desktop?.th?.imageUrl || '',
              linkUrl: campaign.banner.desktop?.th?.linkUrl || '',
              action: campaign.banner.desktop?.th?.action || 'open-modal'
            },
            en: {
              imageUrl: campaign.banner.desktop?.en?.imageUrl || '',
              linkUrl: campaign.banner.desktop?.en?.linkUrl || '',
              action: campaign.banner.desktop?.en?.action || 'open-modal'
            }
          },
          mobile: {
            th: {
              imageUrl: campaign.banner.mobile?.th?.imageUrl || '',
              linkUrl: campaign.banner.mobile?.th?.linkUrl || '',
              action: campaign.banner.mobile?.th?.action || 'open-modal'
            },
            en: {
              imageUrl: campaign.banner.mobile?.en?.imageUrl || '',
              linkUrl: campaign.banner.mobile?.en?.linkUrl || '',
              action: campaign.banner.mobile?.en?.action || 'open-modal'
            }
          }
        } : {
          desktop: { th: { imageUrl: '', linkUrl: '', action: 'open-modal' }, en: { imageUrl: '', linkUrl: '', action: 'open-modal' } },
          mobile: { th: { imageUrl: '', linkUrl: '', action: 'open-modal' }, en: { imageUrl: '', linkUrl: '', action: 'open-modal' } }
        },
        features: campaign.features || {
          bonusCoins: { enabled: false, amount: 0, coinType: 'virtual', claimLimit: 1, claimOncePerUser: true },
          packageTrial: { enabled: false, packageType: 'premium', trialDays: 14 }
        },
        positions: campaign.positions || [{ positionId: CAMPAIGN_POSITIONS.HOMEPAGE_TOP, priority: 1, enabled: true }],
        targeting: campaign.targeting || { roles: ['user', 'farm'], userTypes: ['all'], languages: ['th', 'en'] },
        isActive: campaign.isActive !== undefined ? campaign.isActive : true
      });
    }
  }, [campaign]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const campaignData = {
        name: formData.name,
        type: formData.type,
        startDate: formData.startDate ? new Date(formData.startDate) : null,
        endDate: formData.endDate ? new Date(formData.endDate) : null,
        banner: formData.banner,
        features: formData.features,
        positions: formData.positions,
        targeting: formData.targeting,
        status: campaign?.status || CAMPAIGN_STATUS.DRAFT,
        isActive: formData.isActive !== undefined ? formData.isActive : true
      };

      if (campaign) {
        // Update existing campaign
        await updateCampaign(campaign.id, campaignData);
      } else {
        // Create new campaign
        await createCampaign(campaignData);
      }

      if (onSave) onSave();
    } catch (error) {
      console.error('Error saving campaign:', error);
      alert('เกิดข้อผิดพลาดในการบันทึก: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (path, value) => {
    const keys = path.split('.');
    setFormData(prev => {
      const newData = { ...prev };
      let current = newData;
      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]] = { ...current[keys[i]] };
      }
      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Info */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">ข้อมูลพื้นฐาน</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อ Campaign (ไทย)</label>
            <input
              type="text"
              value={formData.name.th}
              onChange={(e) => updateField('name.th', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อ Campaign (English)</label>
            <input
              type="text"
              value={formData.name.en}
              onChange={(e) => updateField('name.en', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">ประเภท</label>
            <select
              value={formData.type}
              onChange={(e) => updateField('type', e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              {Object.values(CAMPAIGN_TYPES).map(type => (
                <option key={type} value={type}>
                  {type === CAMPAIGN_TYPES.PROMOTION ? 'โปรโมชัน' :
                   type === CAMPAIGN_TYPES.BANNER_RENTAL ? 'เช่า Banner' :
                   type === CAMPAIGN_TYPES.SEASONAL ? 'ตามฤดูกาล' :
                   'ประกาศ'}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">วันที่เริ่ม</label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => updateField('startDate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">วันที่สิ้นสุด</label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => updateField('endDate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Banner Configuration */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Banner Configuration</h3>
        
        <div className="space-y-6">
          {/* Desktop Banners */}
          <div>
            <h4 className="text-md font-medium text-gray-700 mb-3">Desktop Banners</h4>
            <div className="space-y-6">
              {/* Desktop Banner (ไทย) */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <BannerImageUpload
                  label="Desktop Banner (ไทย)"
                  device="desktop"
                  language="th"
                  value={formData.banner.desktop.th.imageUrl}
                  onChange={(url) => updateField('banner.desktop.th.imageUrl', url)}
                />
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      เมื่อคลิก Banner จะทำอะไร? <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.banner.desktop.th.action || 'open-modal'}
                      onChange={(e) => updateField('banner.desktop.th.action', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="open-modal">เปิด Popup รับของแถม (Bonus Coins, Package Trial)</option>
                      <option value="navigate">ไปที่ URL (ในแท็บเดิม)</option>
                      <option value="external-link">ไปที่ URL (เปิดแท็บใหม่)</option>
                    </select>
                  </div>
                  {(formData.banner.desktop.th.action === 'navigate' || formData.banner.desktop.th.action === 'external-link') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Link URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={formData.banner.desktop.th.linkUrl || ''}
                        onChange={(e) => updateField('banner.desktop.th.linkUrl', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com หรือ /marketplace/store/123"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 ตัวอย่าง: https://facebook.com/yourpage หรือ /marketplace/store/your-store-id หรือ https://line.me/R/ti/p/@yourline
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Desktop Banner (English) */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <BannerImageUpload
                  label="Desktop Banner (English)"
                  device="desktop"
                  language="en"
                  value={formData.banner.desktop.en.imageUrl}
                  onChange={(url) => updateField('banner.desktop.en.imageUrl', url)}
                />
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      When clicking Banner, what happens? <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.banner.desktop.en.action || 'open-modal'}
                      onChange={(e) => updateField('banner.desktop.en.action', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="open-modal">Open Popup for Rewards (Bonus Coins, Package Trial)</option>
                      <option value="navigate">Navigate to URL (same tab)</option>
                      <option value="external-link">Navigate to URL (new tab)</option>
                    </select>
                  </div>
                  {(formData.banner.desktop.en.action === 'navigate' || formData.banner.desktop.en.action === 'external-link') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Link URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={formData.banner.desktop.en.linkUrl || ''}
                        onChange={(e) => updateField('banner.desktop.en.linkUrl', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com or /marketplace/store/123"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Example: https://facebook.com/yourpage or /marketplace/store/your-store-id or https://line.me/R/ti/p/@yourline
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Banners */}
          <div>
            <h4 className="text-md font-medium text-gray-700 mb-3">Mobile Banners</h4>
            <div className="space-y-6">
              {/* Mobile Banner (ไทย) */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <BannerImageUpload
                  label="Mobile Banner (ไทย)"
                  device="mobile"
                  language="th"
                  value={formData.banner.mobile.th.imageUrl}
                  onChange={(url) => updateField('banner.mobile.th.imageUrl', url)}
                />
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      เมื่อคลิก Banner จะทำอะไร? <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.banner.mobile.th.action || 'open-modal'}
                      onChange={(e) => updateField('banner.mobile.th.action', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="open-modal">เปิด Popup รับของแถม (Bonus Coins, Package Trial)</option>
                      <option value="navigate">ไปที่ URL (ในแท็บเดิม)</option>
                      <option value="external-link">ไปที่ URL (เปิดแท็บใหม่)</option>
                    </select>
                  </div>
                  {(formData.banner.mobile.th.action === 'navigate' || formData.banner.mobile.th.action === 'external-link') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Link URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={formData.banner.mobile.th.linkUrl || ''}
                        onChange={(e) => updateField('banner.mobile.th.linkUrl', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com หรือ /marketplace/store/123"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 ตัวอย่าง: https://facebook.com/yourpage หรือ /marketplace/store/your-store-id หรือ https://line.me/R/ti/p/@yourline
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile Banner (English) */}
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <BannerImageUpload
                  label="Mobile Banner (English)"
                  device="mobile"
                  language="en"
                  value={formData.banner.mobile.en.imageUrl}
                  onChange={(url) => updateField('banner.mobile.en.imageUrl', url)}
                />
                <div className="mt-4 space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      When clicking Banner, what happens? <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.banner.mobile.en.action || 'open-modal'}
                      onChange={(e) => updateField('banner.mobile.en.action', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="open-modal">Open Popup for Rewards (Bonus Coins, Package Trial)</option>
                      <option value="navigate">Navigate to URL (same tab)</option>
                      <option value="external-link">Navigate to URL (new tab)</option>
                    </select>
                  </div>
                  {(formData.banner.mobile.en.action === 'navigate' || formData.banner.mobile.en.action === 'external-link') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Link URL <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="url"
                        value={formData.banner.mobile.en.linkUrl || ''}
                        onChange={(e) => updateField('banner.mobile.en.linkUrl', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="https://example.com or /marketplace/store/123"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        💡 Example: https://facebook.com/yourpage or /marketplace/store/your-store-id or https://line.me/R/ti/p/@yourline
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign Features */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-2">🎁 ฟีเจอร์ที่ Campaign นี้จะให้กับผู้ใช้</h3>
        <p className="text-sm text-gray-600 mb-4">
          เลือกฟีเจอร์ที่ต้องการให้ผู้ใช้ได้รับเมื่อคลิก Campaign นี้
        </p>
        
        <div className="space-y-4">
          {/* Bonus Coins */}
          <div className="border-2 border-gray-200 p-4 rounded-lg hover:border-green-300 transition-colors">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.features.bonusCoins.enabled}
                onChange={(e) => updateField('features.bonusCoins.enabled', e.target.checked)}
                className="mt-1 rounded w-5 h-5"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-2xl">🪙</span>
                  <span className="font-semibold text-gray-800">ให้ Bonus Coins ฟรี</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  ผู้ใช้จะได้รับ Bonus Coins ฟรีเมื่อคลิก Campaign นี้ (ใช้ได้ในระบบ แต่แลกเป็นเงินสดไม่ได้)
                </p>
                {formData.features.bonusCoins.enabled && (
                  <div className="ml-8 space-y-2 bg-green-50 p-3 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        จำนวน Bonus Coins ที่จะให้
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={formData.features.bonusCoins.amount}
                          onChange={(e) => updateField('features.bonusCoins.amount', parseInt(e.target.value) || 0)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                          min="0"
                          placeholder="เช่น 3000"
                        />
                        <span className="text-sm text-gray-600 whitespace-nowrap">เหรียญ</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 ตัวอย่าง: 3000 = ผู้ใช้จะได้รับ 3,000 Bonus Coins ฟรี
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Package Trial */}
          <div className="border-2 border-gray-200 p-4 rounded-lg hover:border-blue-300 transition-colors">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.features.packageTrial.enabled}
                onChange={(e) => updateField('features.packageTrial.enabled', e.target.checked)}
                className="mt-1 rounded w-5 h-5"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-2xl">⭐</span>
                  <span className="font-semibold text-gray-800">ให้ทดลองใช้ Package ฟรี</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  ผู้ใช้จะได้ทดลองใช้ Premium Package ฟรี (ไม่ต้องจ่ายเงิน) เป็นระยะเวลาที่กำหนด
                </p>
                {formData.features.packageTrial.enabled && (
                  <div className="ml-8 space-y-2 bg-blue-50 p-3 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ระยะเวลาทดลองใช้ (วัน)
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={formData.features.packageTrial.trialDays}
                          onChange={(e) => updateField('features.packageTrial.trialDays', parseInt(e.target.value) || 14)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                          min="1"
                          placeholder="เช่น 14"
                        />
                        <span className="text-sm text-gray-600 whitespace-nowrap">วัน</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 ตัวอย่าง: 14 = ผู้ใช้จะได้ทดลองใช้ Premium Package ฟรี 14 วัน
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Video Call Trial */}
          <div className="border-2 border-gray-200 p-4 rounded-lg hover:border-purple-300 transition-colors">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.features.videoCallTrial?.enabled || false}
                onChange={(e) => updateField('features.videoCallTrial.enabled', e.target.checked)}
                className="mt-1 rounded w-5 h-5"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-2xl">📹</span>
                  <span className="font-semibold text-gray-800">ให้ทดลองใช้ Video Call ฟรี</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  ผู้ใช้จะได้ทดลองใช้ Video Call ฟรี (ไม่ต้องจ่ายเงิน) เป็นจำนวนครั้งที่กำหนด
                </p>
                {formData.features.videoCallTrial?.enabled && (
                  <div className="ml-8 space-y-2 bg-purple-50 p-3 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        จำนวน Video Calls ฟรี
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={formData.features.videoCallTrial?.freeCalls || 0}
                          onChange={(e) => updateField('features.videoCallTrial.freeCalls', parseInt(e.target.value) || 0)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          min="0"
                          placeholder="เช่น 5"
                        />
                        <span className="text-sm text-gray-600 whitespace-nowrap">ครั้ง</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 ตัวอย่าง: 5 = ผู้ใช้จะได้ Video Call ฟรี 5 ครั้ง
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ระยะเวลาทดลองใช้ (วัน)
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={formData.features.videoCallTrial?.trialDays || 0}
                          onChange={(e) => updateField('features.videoCallTrial.trialDays', parseInt(e.target.value) || 0)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                          min="0"
                          placeholder="เช่น 7"
                        />
                        <span className="text-sm text-gray-600 whitespace-nowrap">วัน</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 ตัวอย่าง: 7 = ใช้ได้ภายใน 7 วัน (0 = ไม่จำกัดเวลา)
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Store Boost */}
          <div className="border-2 border-gray-200 p-4 rounded-lg hover:border-orange-300 transition-colors">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.features.storeBoost?.enabled || false}
                onChange={(e) => updateField('features.storeBoost.enabled', e.target.checked)}
                className="mt-1 rounded w-5 h-5"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-2xl">🚀</span>
                  <span className="font-semibold text-gray-800">เพิ่มขีดความสามารถร้านค้า</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  เพิ่มจำนวนสินค้า, รูปภาพ, บัญชีธนาคาร ที่สามารถใช้งานได้
                </p>
                {formData.features.storeBoost?.enabled && (
                  <div className="ml-8 space-y-3 bg-orange-50 p-3 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        เพิ่มจำนวนสินค้า
                      </label>
                      <input
                        type="number"
                        value={formData.features.storeBoost?.maxProducts || 0}
                        onChange={(e) => updateField('features.storeBoost.maxProducts', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        min="0"
                        placeholder="เช่น 50"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        เพิ่มจำนวนรูปปกร้าน
                      </label>
                      <input
                        type="number"
                        value={formData.features.storeBoost?.maxCoverImages || 0}
                        onChange={(e) => updateField('features.storeBoost.maxCoverImages', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        min="0"
                        placeholder="เช่น 5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        เพิ่มจำนวนรูปสินค้า/ชิ้น
                      </label>
                      <input
                        type="number"
                        value={formData.features.storeBoost?.maxProductImages || 0}
                        onChange={(e) => updateField('features.storeBoost.maxProductImages', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        min="0"
                        placeholder="เช่น 5"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ระยะเวลา (วัน)
                      </label>
                      <input
                        type="number"
                        value={formData.features.storeBoost?.duration || 0}
                        onChange={(e) => updateField('features.storeBoost.duration', parseInt(e.target.value) || 0)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                        min="0"
                        placeholder="เช่น 30"
                      />
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Featured Store */}
          <div className="border-2 border-gray-200 p-4 rounded-lg hover:border-yellow-300 transition-colors">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.features.featuredStore?.enabled || false}
                onChange={(e) => updateField('features.featuredStore.enabled', e.target.checked)}
                className="mt-1 rounded w-5 h-5"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <span className="text-2xl">⭐</span>
                  <span className="font-semibold text-gray-800">ร้านขึ้นหน้าแรก/ตำแหน่งพิเศษ</span>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  ร้านของคุณจะแสดงในตำแหน่งพิเศษ (หน้าแรก, หมวดหมู่, search results) เพื่อเพิ่มโอกาสขาย
                </p>
                {formData.features.featuredStore?.enabled && (
                  <div className="ml-8 space-y-2 bg-yellow-50 p-3 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        ระยะเวลา (วัน)
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="number"
                          value={formData.features.featuredStore?.duration || 0}
                          onChange={(e) => updateField('features.featuredStore.duration', parseInt(e.target.value) || 0)}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                          min="0"
                          placeholder="เช่น 7"
                        />
                        <span className="text-sm text-gray-600 whitespace-nowrap">วัน</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        💡 ตัวอย่าง: 7 = ร้านจะขึ้นหน้าแรก 7 วัน
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </label>
          </div>

          {/* Info Box */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <span className="text-xl">💡</span>
              <div className="text-sm text-yellow-800">
                <p className="font-medium mb-1">คำแนะนำ:</p>
                <ul className="list-disc list-inside space-y-1 text-yellow-700">
                  <li>สามารถเลือกทั้ง 2 ฟีเจอร์พร้อมกันได้ (เช่น ให้ Bonus Coins + ทดลองใช้ Package)</li>
                  <li>ถ้าไม่เลือกอะไร = Campaign นี้จะแสดงแค่ Banner เท่านั้น (ไม่มีของแถม)</li>
                  <li>ผู้ใช้จะได้รับฟีเจอร์ที่เลือกเมื่อคลิก Campaign และทำตามขั้นตอน</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          ยกเลิก
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
        >
          {loading ? 'กำลังบันทึก...' : campaign ? 'อัพเดท' : 'สร้าง'}
        </button>
      </div>
    </form>
  );
};

export default CampaignForm;

